import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import moment from "moment";
import { API } from "api";

import AppModal from "components/ui/AppModal";
import RefundForm from "components/RefundOrderForm";
import { UTILS } from "utils";
import ShowOrderDetail from "../modals/ShowOrderDetail";
import { errorNotification } from "utils/Utils";
import { errorHandler } from "api/api/errorHandler";
import AppContainer from "components/ui/AppContainer";
import AppPaginatedTable from "components/ui/AppPaginatedTable";
import { DebouncedSearchInput as AppSearchInput } from "components/ui/AppSearchInput";
import AppSelect from "components/ui/AppSelect.jsx";
import AppIcon from "components/ui/AppIcon";
import AppButton from "components/ui/AppButton";

const SEARCH_BY_KEYS = [
  { value: "ORDER_NUMBER", key: "ORDER_NUMBER", label: "By Order Number" },
  { value: "PHONE_NUMBER", key: "PHONE_NUMBER", label: "By WhatsApp Number" },
];

const STATUS_STYLES = {
  PENDING:    { backgroundColor: "#FFF9E6", color: "#FF8C00" },
  COMPLETED:  { backgroundColor: "#E6F7E6", color: "#22C55E" },
  COMPLETE:   { backgroundColor: "#E6F7E6", color: "#22C55E" },
  ON_THE_WAY: { backgroundColor: "#E6F2FF", color: "#3B82F6" },
  SCHEDULED:  { backgroundColor: "#E6F7E6", color: "#22C55E" },
  REJECTED:   { backgroundColor: "#FFE6E6", color: "#EF4444" },
  PREPARING:  { backgroundColor: "#FFF4E6", color: "#F97316" },
  CANCELLED:  { backgroundColor: "#FFE6E6", color: "#EF4444" },
  CANCELED:   { backgroundColor: "#FFE6E6", color: "#EF4444" },
};

const DEFAULT_STATUS_STYLE = { backgroundColor: "#FFF9E6", color: "#FF8C00" };

const getStatusBadgeStyle = (status) =>
  STATUS_STYLES[String(status || "").toUpperCase()] || DEFAULT_STATUS_STYLE;

const formatStatusLabel = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "ON_THE_WAY") return "On the Way";
  if (normalized === "COMPLETE") return "Completed";
  return String(status || "")
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const OrderHistory = () => {
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const { t } = useTranslation();

  const [orders, setOrders] = useState({
    data: [],
    totalPages: 0,
    currentPage: 1,
  });
  const [rowData, setRowData] = useState({});
  const [loading, setLoading] = useState(false);
  const [refundOrder, setRefundOrder] = useState({ visible: false, loading: false });
  const [open, setOpen] = useState(false);

  // Use a ref instead of state — registering the print fn won't trigger re-renders
  const printFnRef = useRef(null);
  // Only used to enable/disable the Print button
  const [hasPrintFn, setHasPrintFn] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSearchCondition, setSelectedSearchCondition] = useState(
    SEARCH_BY_KEYS[0].value
  );

  const requestIdRef = useRef(0);

  useEffect(() => {
    const zone = moment.tz.guess();
    moment.tz.setDefault(zone);
  }, []);

  const fetchOrders = useCallback(
    async ({ page, search, searchOn }) => {
      const params = { page };
      if (search) {
        params.search = search;
        params.searchOn = searchOn;
      }

      const reqId = ++requestIdRef.current;
      try {
        setLoading(true);
        const result = await API.GET_ORDERS_HISTORY(params);
        if (reqId !== requestIdRef.current) return;

        if (result?.success === 1 && UTILS.isValidObject(["data", "orders"], result)) {
          setOrders({
            data: result.data.orders,
            totalPages: result.data.count,
            currentPage: page,
          });
        } else {
          UTILS.handleErrorNotification(t("Please try again Later"));
        }
      } catch (error) {
        if (reqId !== requestIdRef.current) return;
        errorNotification(
          errorHandler(error?.error?.message || t("Please try again Later"))
        );
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchOrders({
      page: 1,
      search: searchTerm,
      searchOn: selectedSearchCondition,
    });
  }, [selectedSearchCondition, searchTerm]);

  const handlePageChange = (page) => {
    fetchOrders({
      page,
      search: searchTerm,
      searchOn: selectedSearchCondition,
    });
  };

  // Stable callback identity — prevents ShowOrderDetail's effect from re-running.
  const handlePrintReady = useCallback((printFn) => {
    printFnRef.current = printFn || null;
    setHasPrintFn(!!printFn);
  }, []);

  // Reset when detail modal closes
  useEffect(() => {
    if (!open) {
      printFnRef.current = null;
      setHasPrintFn(false);
    }
  }, [open]);

  const handleShowRefundModal = useCallback(() => {
    setRefundOrder((prev) => ({ ...prev, visible: true }));
    setOpen(false);
  }, []);

  const submitRefundOrder = async ({ amount, reason, order }) => {
    setRefundOrder((prev) => ({ ...prev, loading: true }));
    const { _id: orderId, brandId } = order;
    try {
      const result = await API.REFUND_ORDER({ orderId, amount, reason, brandId });
      if (result?.success === 1) {
        UTILS.successNotification(result?.message || t("Refund Requested"));
        fetchOrders({
          page: orders.currentPage,
          search: searchTerm,
          searchOn: selectedSearchCondition,
        });
        setRefundOrder({ loading: false, visible: false });
        setOpen(false);
      } else {
        setRefundOrder((prev) => ({ ...prev, loading: false }));
        UTILS.errorNotification(result?.message || t("Please try again Later"));
      }
    } catch (error) {
      setRefundOrder((prev) => ({ ...prev, loading: false }));
      UTILS.errorNotification(t("Please try again Later"));
    }
  };

  const renderDate = (_value, row) => (
    <div className="px-3 py-1 whitespace-nowrap text-center">
      <h3>{row.orderPlacedTime ? moment.unix(row.orderPlacedTime).format("DD/MM/YYYY") : ""}</h3>
      <h5 className="leading-3 text-12px text-gray-500">
        {row.orderPlacedTime ? moment.unix(row.orderPlacedTime).format("HH:mm:ss") : ""}
      </h5>
    </div>
  );

  const renderOrderDetail = (_val, row) => (
    <Link
      to="#"
      className="mx-1 text-blue-600 cursor-pointer"
      key="edit"
      onClick={() => {
        setOpen(true);
        setRowData(row);
      }}
    >
      <AppIcon name="eye" />
    </Link>
  );

  const columns = [
    {
      title: t("Number"),
      dataIndex: "orderNumber",
      key: "orderNumber",
      fixed: "left",
      sorter: { compare: (a, b) => a.orderNumber - b.orderNumber, multiple: 3 },
    },
    {
      title: t("Price"),
      dataIndex: "totalOrderPrice",
      sorter: { compare: (a, b) => a.totalOrderPrice - b.totalOrderPrice, multiple: 3 },
    },
    {
      title: t("Date"),
      dataIndex: "orderPlacedTime",
      render: renderDate,
      sorter: { compare: (a, b) => a.orderPlacedTime - b.orderPlacedTime, multiple: 2 },
    },
    {
      title: t("Type"),
      dataIndex: "orderType",
      render: (val) => (currentLanguage === "EN" ? val?.EN?.name : val?.AR?.name),
    },
    {
      title: t("Whatsapp Number"),
      dataIndex: "userInfo",
      render: (val) => (
        <div
          dir="ltr"
          className="cursor-pointer text-blue-600 underline hover:text-blue-800"
          onClick={() => UTILS.openWhatsApp(val?.phoneNumber)}
        >
          {val?.phoneNumber}
        </div>
      ),
    },
    {
      title: t("Status"),
      dataIndex: "orderStatus",
      render: (val) => (
        <span
          className="inline-flex min-w-[98px] items-center justify-center rounded-full px-4 py-2 text-sm font-medium"
          style={getStatusBadgeStyle(val)}
        >
          {formatStatusLabel(val)}
        </span>
      ),
    },
    {
      title: t("Store"),
      dataIndex: "storeInfo",
      width: "25%",
      render: (val) => val?.baseLanguage?.name,
    },
    {
      title: t("Detail"),
      dataIndex: "OrderDetail",
      fixed: "right",
      render: renderOrderDetail,
    },
  ];

  return (
    <>
      <AppModal
        open={open}
        onClose={() => setOpen(false)}
        width={820}
        className="app-modal app-modal-order-details"
        maskClosable={false}
        destroyOnClose
        footer={
          <div className="flex w-full items-center justify-start">
            <AppButton
              type="button"
              variant="primaryOutline"
              size="sm"
              className="hidden-print"
              icon={<PrinterOutlined />}
              onClick={() => printFnRef.current?.()}
              disabled={!hasPrintFn}
            >
              {t("Print")}
            </AppButton>
          </div>
        }
      >
        <ShowOrderDetail
          rowData={rowData}
          loading={loading}
          onPrintReady={handlePrintReady}
          showRefundModal={handleShowRefundModal}
        />
      </AppModal>

      <AppModal
        open={refundOrder?.visible}
        width={520}
        onClose={() => setRefundOrder({ loading: false, visible: false })}
        destroyOnClose
      >
        <RefundForm
          loading={refundOrder?.loading}
          order={rowData}
          onSubmit={submitRefundOrder}
          closeModal={() => {
            setRefundOrder({ loading: false, visible: false });
            setOpen(true);
          }}
        />
      </AppModal>

      <AppContainer
        title={t("Orders History")}
        subTitle={t("For more detailed report contact Suppport at with your registered email address")}
        loading={loading}
        containerProps={{ className: "overflow-y-hidden" }}
      >
        <div className="my-3 flex items-end justify-between">
          <div className="w-1/4">
            <AppSelect
              label={t("Search by")}
              value={selectedSearchCondition}
              options={SEARCH_BY_KEYS}
              onChange={(value) => {
                setSelectedSearchCondition(value);
                setSearchTerm("");
              }}
            />
          </div>
          <div className="w-1/4">
            <AppSearchInput
              placeholder={
                selectedSearchCondition === "ORDER_NUMBER"
                  ? t("Order Number")
                  : t("WhatsApp Number")
              }
              value={searchTerm}
              onDebouncedChange={(value) => setSearchTerm(value)}
            />
          </div>
        </div>

        <AppPaginatedTable
          columns={columns}
          data={orders?.data}
          clientSidePagination={false}
          pageSize={orders?.data?.length}
          page={orders.currentPage}
          total={orders.totalPages}
          onRowClick={(row) => {
            setOpen(true);
            setRowData(row);
          }}
          onPageChange={(page) => handlePageChange(page)}
        />
      </AppContainer>
    </>
  );
};

export default OrderHistory;